// Works with the 'NISTAuditLog' helper function to check if a key exists in NIST_LOG_EVENTS (e.g., VIEWING_PAGE).
// Determines whether to use the default title/description or the values passed in as parameters.
// The title/description parameters allow dynamic formatting with variables from the component if needed.
// Consider creating a Confluence diagram that outlines all events logged via NIST.
// This constants object helps devs verify available event keys—VSCode will provide autocomplete suggestions when typing NIST_LOG_EVENTS.
export const NIST_LOG_EVENTS = {
  // ERROR CODE LOGS - Besides 'ERROR_RESPONSE', these are all the possible network error codes
  ERROR_RESPONSE: {
    key: 'ERROR_RESPONSE',
    title: 'Response Error',
    desc: 'There was an error included in the response from the request.',
  },
  ERR_NETWORK: {
    key: 'NETWORK_ERROR',
    title: 'Network Error',
    desc: 'There was a network error when sending the request to the designated API.',
  },
  ECONNABORTED: {
    key: 'TIMEOUT_ERROR',
    title: 'Timeout Error',
    desc: 'The request took too long to complete and was aborted.',
  },
  ERR_CANCELED: {
    key: 'CANCELED_ERROR',
    title: 'Canceled Request',
    desc: 'The request was canceled before completion.',
  },
  ERR_FR_TOO_MANY_REDIRECTS: {
    key: 'TOO_MANY_REDIRECTS',
    title: 'Too Many Redirects',
    desc: 'The request failed due to too many HTTP redirects.',
  },
  ENOTFOUND: {
    key: 'DNS_LOOKUP_FAILED',
    title: 'DNS Lookup Failed',
    desc: 'The domain name could not be resolved (DNS failure).',
  },
  ECONNREFUSED: {
    key: 'CONNECTION_REFUSED',
    title: 'Connection Refused',
    desc: 'The connection was refused by the target server.',
  },
  ETIMEDOUT: {
    key: 'TIMEOUT',
    title: 'Connection Timeout',
    desc: 'The connection attempt timed out before completing.',
  },
  EPIPE: {
    key: 'BROKEN_PIPE',
    title: 'Broken Pipe',
    desc: 'The connection was broken unexpectedly during communication.',
  },
  EHOSTUNREACH: {
    key: 'HOST_UNREACHABLE',
    title: 'Host Unreachable',
    desc: 'The target host is unreachable from the client.',
  },

  // GENERAL LOGS
  VIEWING_PAGE: {
    key: 'VIEWING_PAGE',
    title: 'User is Viewing a Page',
    desc: 'User has accessed this page and is viewing it.',
  },

  // DASHBOARD DROPDOWN LOGS
  USER_UPDATED_AVATAR_IMAGE: {
    key: 'USER_UPDATED_AVATAR_IMAGE',
    title: 'Updated Profile Avatar Image',
    desc: 'User has updated their profiles avatar image.',
  },
  USER_SWITCH_TAX_YEAR: {
    key: 'USER_SWITCH_TAX_YEAR',
    title: 'User Switching Tax Year',
    desc: 'User is switching tax years.',
  },
  USER_SWITCH_OFFICE: {
    key: 'USER_SWITCH_OFFICE',
    title: 'User Switching Offices',
    desc: 'User is switching offices.',
  },
  USER_TOGGLED_TRAINING_MODE: {
    key: 'USER_TOGGLED_TRAINING_MODE',
    title: 'User Toggling Training Mode',
    desc: 'The user is toggling training mode.',
  },
  UPDATING_PREPARER_SIG: {
    key: 'UPDATING_PREPARER_SIG',
    title: 'User Updating Preparer Signature',
    desc: 'The user has updated their preparer signature.',
  },
  UPDATING_ERO_SIG: {
    key: 'UPDATING_ERO_SIG',
    title: 'User Updating ERO Signature',
    desc: 'The user has updated their ERO signature.',
  },
  ACCESSING_WALLET: {
    key: 'ACCESSING_WALLET',
    title: 'User Accessing Wallet',
    desc: 'The user is accessing a wallet.',
  },
  UPDATE_LOGIN_PREFS: {
    key: 'UPDATE_LOGIN_PREFS',
    title: 'User Updated Login Preferences',
    desc: 'The user has updated the login preferences.',
  },

  // TAX RETURNS PAGE
  VIEW_TAX_RETURN: {
    key: 'VIEW_TAX_RETURN',
    title: 'View Tax Return',
    desc: 'User is viewing a tax return.',
  },
  VIEW_WIZARD_RETURN: {
    key: 'VIEW_WIZARD_RETURN',
    title: 'View Wizard Return',
    desc: 'User is viewing a wizard return.',
  },
  CREATE_TAX_RETURN: {
    key: 'CREATE_TAX_RETURN',
    title: 'Create Tax Return',
    desc: 'User is creating a tax return.',
  },
  TOGGLE_RETURN_LIST: {
    key: 'TOGGLE_RETURN_LIST',
    title: 'Toggling Tax Returns List',
    desc: 'User is toggling the return list.',
  },
  TRANSFER_RETURN_OWNERSHIP: {
    key: 'TRANSFER_RETURN_OWNERSHIP',
    title: 'Transfer Return Ownership',
    desc: 'User is transferring return to another preparer.',
  },
  CHANGE_OWNER_OF_RETURN: {
    key: 'CHANGE_OWNER_OF_RETURN',
    title: 'Change Owner of Return',
    desc: 'User is change the owner of a return.',
  },
  VIEW_RETURN_PAYMENT_HISTORY: {
    key: 'VIEW_RETURN_PAYMENT_HISTORY',
    title: 'View Return Payment History',
    desc: 'User is viewing the payment history for a return.',
  },
  // WE STILL NEED MAKE PAYMENT MODAL IMPLEMENTED - OUTSIDE+INSIDE
  VIEW_IMPORT_RETURNS_LIST: {
    key: 'VIEW_IMPORT_RETURNS_LIST',
    title: 'View Import Returns List',
    desc: 'User is viewing the import returns list.',
  },
  IMPORTING_RETURN: {
    key: 'IMPORTING_RETURN',
    title: 'Importing Return',
    desc: 'User is importing a return.',
  },

  // LOGIN PAGE
  SEND_FORGOT_PASSWORD_CODE: {
    key: 'SEND_FORGOT_PASSWORD_CODE',
    title: 'User Sent a Forgot Password Code',
    desc: 'User sent a forgot password code for a login.',
  },
  SEND_FORGOT_USERNAME_EMAIL: {
    key: 'SEND_FORGOT_USERNAME_EMAIL',
    title: 'User Sent a Forgot Username Email',
    desc: 'User sent a forgot username email.',
  },
  RESET_LOGIN_PASSWORD: {
    key: 'RESET_LOGIN_PASSWORD',
    title: 'User Reset Login Password',
    desc: 'User has reset a password for a login.',
  },
  USER_OPTED_MFA_METHOD: {
    key: 'USER_OPTED_MFA_METHOD',
    title: 'User Opted for an MFA Method',
    desc: 'User has opted for a MFA method.',
  },
  USER_UPDATED_PASSWORD: {
    key: 'USER_UPDATED_PASSWORD',
    title: 'User Updated Password',
    desc: 'User has updated the password for their login.',
  },
  USER_GENERATE_NEW_QR_CODE: {
    key: 'USER_GENERATE_NEW_QR_CODE',
    title: 'User Generated a New QR Code',
    desc: 'User has generated a new QR Code.',
  },

  // USER LOOKUP PAGE
  UNLOCK_LOGIN: {
    key: 'UNLOCK_LOGIN',
    title: 'Unlock Login',
    desc: 'User is unlocking a login.',
  },
  SEND_RESET_LOGIN_REQUEST: {
    key: 'SEND_RESET_LOGIN_REQUEST',
    title: 'Send Reset Login Password Request',
    desc: 'User has sent a reset password request to a logins email.',
  },
  RESEND_LOGIN_ACTIVATION_LINK: {
    key: 'RESEND_LOGIN_ACTIVATION_LINK',
    title: 'Resend Login Activation Link',
    desc: 'User is resending the login activation link.',
  },
  // need to verify if there are other ways to impersonate a login other than the User Lookup page - if so, we should integrate this log into the redux func rather than just the User Lookup page
  IMPERSONATE_LOGIN: {
    key: 'IMPERSONATE_LOGIN',
    title: 'Impersonate Login',
    desc: 'An admin/tech user is impersonating a login through the User Lookup page.',
  },

  // WIDGETS
  USER_ADDED_WIDGET: {
    key: 'USER_ADDED_WIDGET',
    title: 'Dashboard Widget Added',
    desc: 'User has added a widget.',
  },

  // TECH LOGINS PAGE
  UNLOCK_TECH_LOGIN: {
    key: 'UNLOCK_TECH_LOGIN',
    title: 'Unlock Tech Login',
    desc: 'User is unlocking a tech login.',
  },
  DEACTIVATE_TECH_LOGIN: {
    key: 'DEACTIVATE_TECH_LOGIN',
    title: 'Deactivating Tech Login',
    desc: 'User is deactivating a tech login.',
  },

  // MOVE RETURN PAGE
  MOVE_RETURN: {
    key: 'MOVE_RETURN',
    title: 'Moving Return',
    desc: 'Return is being moved from one efin to another under the same parent user ID.',
  },

  // Packages page
  UPDATE_AVAILABLE_PACKAGES: {
    key: 'UPDATE_AVAILABLE_PACKAGES',
    title: 'Update Available Packages',
    desc: 'An admin is updating the available packages.',
  },

  // Create office/mo/res;;er
  CREATE_NEW_OFFICE: {
    key: 'CREATE_NEW_OFFICE',
    title: 'Creating New Office',
    desc: 'User is creating a new office.',
  },
  CREATE_NEW_MULTI_OFFICE: {
    key: 'CREATE_NEW_MULTI_OFFICE',
    title: 'Creating New Multi-Office',
    desc: 'User is creating a new multi-office.',
  },
  CREATE_NEW_RESELLER: {
    key: 'CREATE_NEW_RESELLER',
    title: 'Creating New Reseller',
    desc: 'User is creating a new reseller.',
  },

  // CHECKS PAGE
  PRINT_READY_TO_PRINT_CHECK: {
    key: 'PRINT_READY_TO_PRINT_CHECK',
    title: 'Printing Ready To Print Check',
    desc: 'User is printing a ready to print check.',
  },
  VOID_REGISTERED_CHECK: {
    key: 'VOID_REGISTERED_CHECK',
    title: 'Voiding Registered Check',
    desc: 'User is voiding a check that has been registered.',
  },
  CHECK_REPRINT: {
    key: 'CHECK_REPRINT',
    title: 'Reprinting Checks',
    desc: 'User is reprinting checks.',
  },

  // REPORTS
  RUNNING_REPORT: {
    key: 'RUNNING_REPORT',
    title: 'Running Report',
    desc: 'User is running a report.',
  },
  DELETE_CUSTOM_REPORT: {
    key: 'DELETE_CUSTOM_REPORT',
    title: 'Delete Custom Report',
    desc: 'User is deleting a custom report.',
  },

  // SETTINGS PAGE
  UPDATE_OFFICE_SETUP: {
    key: 'UPDATE_OFFICE_SETUP',
    title: 'Update Office Setup',
    desc: 'User is updating the office setup.',
  },
  UPDATE_ERO_SETUP: {
    key: 'UPDATE_ERO_SETUP',
    title: 'Update ERO Setup',
    desc: 'User is updating the ERO setup.',
  },
  UPDATE_PREPARER_SETUP: {
    key: 'UPDATE_PREPARER_SETUP',
    title: 'Update Preparer Setup',
    desc: 'User is updating the Preparer setup.',
  },
  UPDATE_BILLING_SETUP: {
    key: 'UPDATE_BILLING_SETUP',
    title: 'Update Billing Setup',
    desc: 'User is updating the Billing setup.',
  },
  UPDATE_BUSINESS_BILLING_SETUP: {
    key: 'UPDATE_BUSINESS_BILLING_SETUP',
    title: 'Update Business Billing Setup',
    desc: 'User is updating the Business Billing setup.',
  },
  UPDATE_EFILING_SETUP: {
    key: 'UPDATE_EFILING_SETUP',
    title: 'Update EFiling Setup',
    desc: 'User is updating the EFiling setup.',
  },
  UPDATE_PRINTING_SETUP: {
    key: 'UPDATE_PRINTING_SETUP',
    title: 'Update Printing Setup',
    desc: 'User is updating the Printing setup.',
  },
  UPDATE_DEFAULTS_SETUP: {
    key: 'UPDATE_DEFAULTS_SETUP',
    title: 'Update Defaults Setup',
    desc: 'User is updating the Defaults setup.',
  },
  UPDATE_AUDIT_SETUP: {
    key: 'UPDATE_AUDIT_SETUP',
    title: 'Update Audit Setup',
    desc: 'User is updating the Audit setup.',
  },
  DELETE_LOGIN_AND_OR_PREPARER: {
    key: 'DELETE_LOGIN_AND_OR_PREPARER',
    title: 'Deleting Login and/or Preparer',
    desc: 'User is deleting a login and/or preparer.',
  },
  CREATE_PREPARER: {
    key: 'CREATE_PREPARER',
    title: 'Create Preparer',
    desc: 'User created a new preparer.',
  },
  ACTIVATE_NEW_CHECK: {
    key: 'ACTIVATE_NEW_CHECK',
    title: 'Activate New Check',
    desc: 'User is activating a new check.',
  },
  UPDATE_CHECK: {
    key: 'UPDATE_CHECK',
    title: 'Update Check',
    desc: 'User is updating a check.',
  },
  CREATE_LOGIN: {
    key: 'CREATE_LOGIN',
    title: 'Create Login',
    desc: 'User is creating a login.',
  },
  UPDATE_LOGIN: {
    key: 'UPDATE_LOGIN',
    title: 'Update Login',
    desc: 'User is updating a login.',
  },
  ADD_RESTRICTED_FIELD: {
    key: 'ADD_RESTRICTED_FIELD',
    title: 'Add Restricted Field',
    desc: 'User is adding a restricted field.',
  },
  DELETE_RESTRICTED_FIELD: {
    key: 'DELETE_RESTRICTED_FIELD',
    title: 'Delete Restricted Field',
    desc: 'User is deleting a restricted field.',
  },
  ADD_DATABASE_RECORD: {
    key: 'ADD_DATABASE_RECORD',
    title: 'Add Database Record',
    desc: 'User is adding a database record.',
  },
  DELETE_DATABASE_RECORD: {
    key: 'DELETE_DATABASE_RECORD',
    title: 'Delete Database Record',
    desc: 'User is deleting a database record.',
  },
  UPDATE_ANCILLARY_PRODUCT: {
    key: 'UPDATE_ANCILLARY_PRODUCT',
    title: 'Update Ancillary Product',
    desc: 'User has updated an ancillary product.',
  },
  ASSIGN_LICENSE: {
    key: 'ASSIGN_LICENSE',
    title: 'Assign License',
    desc: 'User has assigned a license to an office.',
  },
  UNAPPLY_LICENSE: {
    key: 'UNAPPLY_LICENSE',
    title: 'Unapply License',
    desc: 'User has unapplied a license from an office.',
  },
  ADD_PRODUCT_LICENSE: {
    key: 'ADD_PRODUCT_LICENSE',
    title: 'Add Product License',
    desc: 'User has added a product license.',
  },
  VIEWING_SETTINGS_PAGE: {
    key: 'VIEWING_SETTINGS_PAGE',
    title: 'Viewing Settings',
    desc: 'User is viewing a page in the settings.',
  },

  // PAYMENTS
  VIEWING_PAYMENTS: {
    key: 'VIEWING_PAYMENTS',
    title: 'Viewing Payments',
    desc: 'User is viewing the payments page.',
  },
  VIEWING_PAYMENT_TRANSACTION: {
    key: 'VIEWING_PAYMENT_TRANSACTION',
    title: 'Viewing Payment Transaction',
    desc: 'User is viewing a payment transaction.',
  },
  PRINTING_PAYMENT_TRANSACTION: {
    key: 'PRINTING_PAYMENT_TRANSACTION',
    title: 'Printing Payment Transaction',
    desc: 'User is printing a payment transaction.',
  },
  MANUAL_PAYMENT: {
    key: 'MANUAL_PAYMENT',
    title: 'Manual Payment',
    desc: 'User is processing a manual payment.',
  },
  PAYJUNCTION_PAYMENT: {
    key: 'PAYJUNCTION_PAYMENT',
    title: 'Payjunction Payment',
    desc: 'User is processing a payjunction payment.',
  },
  INVOICE_PAYMENT: {
    key: 'INVOICE_PAYMENT',
    title: 'Invoice Payment',
    desc: 'User is processing a invoice payment.',
  },

  // REMOTE SIGNATURE
  PREPARER_RMS_REQUEST: {
    key: 'PREPARER_RMS_REQUEST',
    title: 'Preparer Remote Signature Request',
    desc: "User is sending a remote signature request to a preparer's email.",
  },
  REMOTE_SIGNATURE_VERIFICATION: {
    key: 'REMOTE_SIGNATURE_VERIFICATION',
    title: 'Remote Signature Verification',
    desc: 'User verifying their remote signature.',
  },
  SIGNING_REMOTE_SIGNATURE: {
    key: 'SIGNING_REMOTE_SIGNATURE',
    title: 'Signing Remote Signature',
    desc: 'User is signing their remote signature.',
  },

  // Remote Documents
  VERIFIED_REMOTE_DOC_REQUEST: {
    key: 'VERIFIED_REMOTE_DOC_REQUEST',
    title: 'Verified Remote Document Request',
    desc: 'User has verified their remote document request.',
  },
  SAVE_REQUESTED_DOCUMENT: {
    key: 'SAVE_REQUESTED_DOCUMENT',
    title: 'Save Requested Remote Document',
    desc: 'User has saved their requested document.',
  },
};
